import Seo from "@/components/Seo";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { FOUNDER } from "@/lib/data";

export default function Founder() {
  return (
    <div data-testid="founder-page">
      <Seo
        title="Our Founder — Shamin Mohamed Sr. | Saver Plus Lighting Corp (SPLC), Toronto"
        description="In memory of Shamin Mohamed Sr. (1954–2025), the visionary founder of Saver Plus Lighting Corp. whose dedication, honesty, and personal service built the company and its lasting customer relationships."
        path="/founder"
      />
      {/* Hero */}
      <section className="pt-40 md:pt-56 pb-16 md:pb-20 max-w-[1400px] mx-auto px-5 md:px-10 text-center">
        <p className="text-xs uppercase tracking-[0.28em] text-gold/90 font-semibold mb-8">In Memory</p>
        <h1 className="font-display uppercase font-medium text-[15vw] md:text-[9vw] leading-[0.85] tracking-tighter">
          Shamin<br />Mohamed Sr.
        </h1>
        <p className="mt-8 text-sm uppercase tracking-[0.2em] text-white/50">{FOUNDER.role}</p>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-16 md:pb-24">
        {/* Portrait + opening story */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="overflow-hidden aspect-[3/4] bg-[#121212] border border-white/10">
              <img
                src={FOUNDER.photo}
                alt="Portrait of Shamin Mohamed Sr., founder of Saver Plus Lighting Corp."
                className="w-full h-full object-cover object-center"
                data-testid="founder-photo"
              />
            </div>
            <div className="mt-5 text-center">
              <p className="text-xs uppercase tracking-[0.22em] text-white/40">In Loving Memory</p>
              <p className="mt-2 font-display text-xl md:text-2xl tracking-tight text-gold">{FOUNDER.dates}</p>
            </div>
          </div>

          <div className="lg:col-span-6 lg:col-start-7 space-y-6">
            {FOUNDER.story.map((p, i) => (
              <Reveal key={i} delay={i * 0.04}>
                <p className={`leading-relaxed text-justify ${i === 0 ? "text-lg text-white/80" : "text-white/60"}`}>{p}</p>
              </Reveal>
            ))}
          </div>
        </div>

        {/* To those who knew him */}
        <div className="mt-20 md:mt-28 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          <div className="lg:col-span-4">
            <Reveal>
              <h2 className="font-display uppercase text-2xl md:text-3xl tracking-tight leading-tight">
                To those who knew him
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 space-y-6">
            {FOUNDER.knewHim.map((p, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <p className="text-white/60 leading-relaxed text-justify">{p}</p>
              </Reveal>
            ))}
          </div>
        </div>

        {/* His words to live by */}
        <Reveal>
          <figure className="mt-20 md:mt-28 max-w-4xl mx-auto text-center" data-testid="founder-quote">
            <p className="text-xs uppercase tracking-[0.28em] text-gold/80 font-semibold mb-8">His words to live by</p>
            <blockquote className="font-display text-3xl md:text-5xl leading-[1.15] tracking-tight text-balance">
              &ldquo;{FOUNDER.quote}&rdquo;
            </blockquote>
            <figcaption className="mt-8 text-sm uppercase tracking-[0.2em] text-white/50">— {FOUNDER.name}</figcaption>
          </figure>
        </Reveal>

        {/* In loving memory */}
        <Reveal>
          <div className="mt-24 md:mt-32 pt-16 border-t border-white/10 text-center" data-testid="founder-memorial">
            <p className="text-xs uppercase tracking-[0.28em] text-white/40 mb-6">{FOUNDER.memorial[0]}</p>
            <p className="font-display uppercase text-4xl md:text-6xl tracking-tight leading-[0.95] text-gold">
              {FOUNDER.memorial[1]}
            </p>
            <p className="mt-8 text-lg text-white/60 max-w-xl mx-auto leading-relaxed">{FOUNDER.memorial[2]}</p>
            <p className="mt-3 text-lg text-white/80 font-display uppercase tracking-tight">{FOUNDER.memorial[3]}</p>
          </div>
        </Reveal>

        {/* Continue his legacy */}
        <Reveal>
          <div className="mt-20 md:mt-28 text-center">
            <p className="text-white/60 mb-6 max-w-xl mx-auto leading-relaxed">
              We carry his vision forward every day. Learn more about the company he built.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/about"
                data-testid="founder-about-cta"
                className="inline-flex items-center gap-2 border border-white/20 text-sm uppercase tracking-[0.14em] px-6 py-4 hover:border-gold hover:text-gold transition-colors duration-300"
              >
                Our Story
              </Link>
              <Link
                to="/contact"
                data-testid="founder-quote-cta"
                className="inline-flex items-center gap-2 bg-gold text-white text-sm uppercase tracking-[0.14em] font-semibold px-7 py-4 hover:bg-[#b00000] transition-colors duration-300"
              >
                Request a Quote <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
